import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';

export const useArtistAuth = () => {
  const [user, setUser] = useState(null);
  const [isArtist, setIsArtist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('useArtistAuth: Getting initial session...');
        
        // Check for existing session first
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('useArtistAuth: Session check result:', { session: !!session, user: !!session?.user, error });
        
        if (error) {
          console.error('useArtistAuth: Error getting initial session:', error);
          setError(error.message);
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          console.log('useArtistAuth: Session found, checking artist role...');
          clearLoadingTimeout();
          await checkArtistRole(session.user);
        } else {
          // Also check if there's a current user
          const { data: { user } } = await supabase.auth.getUser();
          console.log('useArtistAuth: Current user check result:', { user: !!user });
          if (user) {
            console.log('useArtistAuth: Current user found, checking artist role...');
            clearLoadingTimeout();
            await checkArtistRole(user);
          } else {
            console.log('useArtistAuth: No session or user found');
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('useArtistAuth: Error getting initial session:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    // Handle tab visibility changes to prevent stuck loading states
    const handleVisibilityChange = () => {
      if (!document.hidden && user && loading) {
        console.log('Tab became visible, checking if loading state is stuck');
        // If we have a user but are still loading, something might be stuck
        setTimeout(() => {
          if (loading) {
            console.warn('Loading state stuck after tab switch, forcing to false');
            setLoading(false);
          }
        }, 2000);
      }
    };

    // Handle auth changes more reliably
    let loadingTimeoutId = null;
    
    const startLoadingTimeout = () => {
      loadingTimeoutId = setTimeout(() => {
        if (loading) {
          console.warn('useArtistAuth: Loading timeout reached, forcing loading state to false');
          setLoading(false);
        }
      }, 3000); // Increased timeout to allow for proper auth flow
    };

    const clearLoadingTimeout = () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
    };

    // Start the initial timeout
    startLoadingTimeout();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('useArtistAuth: Auth state changed:', event, !!session);
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('useArtistAuth: User signed in, checking artist role...');
          // Clear timeout when authentication succeeds
          clearLoadingTimeout();
          await checkArtistRole(session.user);
        } else if (event === 'SIGNED_OUT') {
          console.log('useArtistAuth: User signed out');
          setUser(null);
          setIsArtist(false);
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Handle token refresh without changing loading state
          console.log('useArtistAuth: Token refreshed');
          setUser(session.user);
        } else if (event === 'USER_UPDATED' && session?.user) {
          // Handle user updates without changing loading state
          console.log('useArtistAuth: User updated');
          setUser(session.user);
        }
      }
    );

    getInitialSession();

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      clearLoadingTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Remove loading and user dependencies to prevent infinite loops

  const checkArtistRole = async (userData) => {
    try {
      setError(null);

      // Set user immediately
      setUser(userData);
      
      // For now, allow any authenticated user to access artist submissions
      // This can be made more restrictive later if needed
      setIsArtist(true);
      
      console.log('useArtistAuth: User authenticated, allowing artist access');
      console.log('useArtistAuth: Setting loading to false, user ID:', userData.id);
      
      setLoading(false);
    } catch (err) {
      console.error('useArtistAuth: Error checking artist role:', err);
      setError(err.message);
      // Still allow access even if checks fail
      setUser(userData);
      setIsArtist(true);
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        await checkArtistRole(data.user);
      }

      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });

      if (error) throw error;

      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsArtist(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const refreshUser = async () => {
    if (user) {
      await checkArtistRole(user);
    }
  };

  return {
    user,
    isArtist,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    refreshUser,
    authenticated: !!user
  };
};
